import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Breadcrum from '../../components/breadcrum'
import { createSlider, getSliderById, updateSlider } from '../../api/sliderApi'

const AddSlider = () => {
  const [searchParams] = useSearchParams()
  const sliderId = searchParams.get('id')
  const navigate = useNavigate()
  const isEditMode = Boolean(sliderId)

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      pageUrl: '',
      image: null,
    },
  })

  // Fetch slider data if in edit mode
  const {
    data: sliderData,
    isLoading: isLoadingSlider,
    isError: isSliderError,
  } = useQuery({
    queryKey: ['slider', sliderId],
    queryFn: () => getSliderById(sliderId),
    enabled: isEditMode,
    // REMOVED onSuccess and onError - these are deprecated in newer versions
  })

  // Handle slider data loading and errors
  useEffect(() => {
    if (isEditMode && sliderData) {
      // Populate form with existing data when available
      setValue('title', sliderData.title || '')
      setValue('description', sliderData.description || '')
      setValue('pageUrl', sliderData.pageUrl || '')
      // Don't set image as it's a file input
    }
  }, [sliderData, isEditMode, setValue])

  // Handle slider loading error
  useEffect(() => {
    if (isSliderError) {
      toast.error('Failed to load slider data')
      navigate('/dealdashboard/slider')
    }
  }, [isSliderError, navigate])

  // Watch the image file to display preview
  const imageFile = watch('image')
  const imagePreview =
    imageFile && imageFile.length > 0
      ? URL.createObjectURL(imageFile[0])
      : isEditMode && sliderData?.imageUrl
      ? sliderData.imageUrl
      : null

  // Create mutation
  const createSliderMutation = useMutation({
    mutationFn: createSlider,
    onSuccess: () => {
      toast.success('Slider added successfully!')
      reset() // Reset form after successful submission
      navigate('/dealdashboard/slider')
    },
    onError: (error) => {
      console.error('Create slider error:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred while adding the slider'
      toast.error(errorMessage)
    },
  })

  // Update mutation
  const updateSliderMutation = useMutation({
    mutationFn: (data) => updateSlider(sliderId, data),
    onSuccess: () => {
      toast.success('Slider updated successfully!')
      navigate('/dealdashboard/slider')
    },
    onError: (error) => {
      console.error('Update slider error:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred while updating the slider'
      toast.error(errorMessage)
    },
  })

  // Form submission handler with better error handling
  const onSubmit = async (data) => {
    try {
      // Validate that we have an image for create mode
      if (!isEditMode && (!data.image || data.image.length === 0)) {
        toast.error('Please select an image')
        return
      }

      // Validate image type
      if (data.image && data.image.length > 0) {
        const file = data.image[0]
        const validTypes = [
          'image/jpeg',
          'image/jpg',
          'image/png',
          'image/gif',
          'image/webp',
        ]
        if (!validTypes.includes(file.type)) {
          toast.error('Please select a valid image file (JPEG, PNG, GIF, WebP)')
          return
        }

        // Check file size (e.g., max 5MB)
        const maxSize = 5 * 1024 * 1024 // 5MB
        if (file.size > maxSize) {
          toast.error('Image size should be less than 5MB')
          return
        }
      }

      if (isEditMode) {
        await updateSliderMutation.mutateAsync(data)
      } else {
        await createSliderMutation.mutateAsync(data)
      }
    } catch (error) {
      console.error('Form submission error:', error)
      // Error is already handled in mutation's onError
    }
  }

  // Handle image change with validation
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]

      // Validate file type
      const validTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
      ]
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file')
        e.target.value = '' // Clear the input
        return
      }

      // Validate file size
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        toast.error('Image size should be less than 5MB')
        e.target.value = '' // Clear the input
        return
      }

      setValue('image', e.target.files)
    }
  }

  // Update page title based on mode
  useEffect(() => {
    document.title = isEditMode ? 'Edit Slider' : 'Add Slider'
  }, [isEditMode])

  // Cleanup image preview URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview)
      }
    }
  }, [imagePreview])

  // Show loading state while fetching slider data in edit mode
  if (isEditMode && isLoadingSlider) {
    return (
      <>
        <Breadcrum
          heading="Slider"
          subheading={isEditMode ? 'Edit Slider' : 'Add Slider'}
        />
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="ml-2 text-gray-500">Loading slider data...</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Breadcrum
        heading="Slider"
        subheading={isEditMode ? 'Edit Slider' : 'Add Slider'}
      />
      <div className="sm:w-[98%] mx-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Slider Heading */}
          <div className="bg-white text-left p-4 rounded-lg shadow mb-4">
            <h2 className="text-lg font-medium">Slider Heading</h2>
            <input
              type="text"
              className={`border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded p-2 mt-2 w-full text-lg focus:border-orange-500 focus:outline-none`}
              {...register('title', {
                required: 'Heading is required',
              })}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Slider Image */}
          <div className="bg-white text-left p-4 rounded-lg shadow mb-4">
            <h2 className="text-lg font-medium">Slider Image</h2>
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-60 rounded-lg border object-cover"
                />
                {isEditMode && !imageFile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Current image shown. Upload a new one to replace it.
                  </p>
                )}
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              className={`p-2 mt-2 w-full ${
                errors.image ? 'border border-red-500 rounded' : ''
              }`}
              onChange={handleImageChange}
              {...register('image', {
                required: isEditMode ? false : 'Image is required',
              })}
            />
            <p className="text-sm text-gray-500 mt-1">
              Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB
            </p>
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">
                {errors.image.message}
              </p>
            )}
          </div>

          {/* Slider Content */}
          <div className="bg-white text-left p-4 rounded-lg shadow mb-4">
            <h2 className="text-lg font-medium">Slider Content</h2>
            <textarea
              className={`border ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              } rounded p-2 mt-2 w-full focus:border-orange-500 focus:outline-none`}
              rows="4"
              {...register('description', {
                required: 'Content is required',
              })}
            ></textarea>
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Slider URL */}
          <div className="bg-white text-left p-4 rounded-lg shadow mb-4">
            <h2 className="text-lg font-medium">Slider URL</h2>
            <input
              type="text"
              placeholder="/example"
              className={`border ${
                errors.pageUrl ? 'border-red-500' : 'border-gray-200'
              } rounded p-2 mt-2 w-full text-lg focus:border-orange-500 focus:outline-none`}
              {...register('pageUrl', {
                required: false,
              })}
            />
            {errors.pageUrl && (
              <p className="text-red-500 text-sm mt-1">
                {errors.pageUrl.message}
              </p>
            )}
          </div>

          <div className="flex gap-4 mb-6">
            <button
              type="submit"
              disabled={
                createSliderMutation.isPending || updateSliderMutation.isPending
              }
              className="bg-orange-500 text-white px-6 py-2 rounded hover:bg-orange-600 transition-colors disabled:bg-orange-300 disabled:cursor-not-allowed"
            >
              {createSliderMutation.isPending || updateSliderMutation.isPending
                ? isEditMode
                  ? 'Updating...'
                  : 'Adding...'
                : isEditMode
                ? 'Update Slider'
                : 'Add Slider'}
            </button>

            <button
              type="button"
              onClick={() => {
                if (isEditMode && sliderData) {
                  // Reset to original data in edit mode
                  setValue('title', sliderData.title)
                  setValue('description', sliderData.description)
                  setValue('pageUrl', sliderData.pageUrl)
                  setValue('image', null)
                } else {
                  // Reset to empty in create mode
                  reset()
                }
              }}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300 transition-colors"
            >
              Reset
            </button>

            <button
              type="button"
              onClick={() => navigate('/dealdashboard/slider')}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default AddSlider
